// External Imports
import { useContext, useEffect, useState } from "react";
import {
  MdDocumentScanner,
  MdDriveEta,
  MdPayments,
  MdPeopleAlt,
} from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { startOfYear } from "date-fns";

/** ========================================================================
 * * CustomerScore Component
 * This component renders information about a customer's record.
 * ========================================================================== */

const samsaraDriverLink =
  "https://cloud.samsara.com/o/6005413/fleet/reports/safety/dashboard/driver/";
const startMs = startOfYear(new Date()).getTime();
const endMs = Date.now();
// From soY to cT
const duration = (endMs - startMs) / 1000;

export default function CustomerScore({ customer }) {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});

  /* ====================== GET SCORES ====================== */
  const getScores = async () => {
    try {
      setLoading(true);
      const payment = getPaymentScores(customer.payments);
      let driving = 100;
      let res = await requestAdminAPI("get-safety-score", {
        customerId: customer.id,
      });
      if (!res.score) driving = 100;
      else driving = res.score;
      const behavior = await getBehaviorScore(customer.id);
      const background = getBackgroundScores();
      const average = Math.ceil(
        (driving * 30 + payment * 50 + behavior * 10 + background * 10) / 100
      );
      setScores({
        payment,
        driving,
        behavior,
        background,
        average,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== GET PAYMENT SCORES ====================== */
  const getPaymentScores = (payments) => {
    let paidOnTimeAmount = 0;
    let paidAmount = 0;
    let totalAmount = 0;

    // Iterate through payments
    payments.forEach((payment) => {
      totalAmount += payment.amount;
      if (payment.status === "succeeded") paidAmount += payment.amount;
      // If payment was an invoice and was paid
      if (payment.type === "payment") paidOnTimeAmount += payment.amount;
      else if (payment.datePaid) {
        // Increase paidOnTime amount if payment was on time
        if (new Date(payment.datePaid) <= new Date(payment.dueDate)) {
          paidOnTimeAmount += payment.amount;
        }
        // If payment was not an invoice, increment paidOnTime amount
      }
    });

    let onTime = 0;
    // If no payments yet, score is max
    if (!payments.length) onTime = 100;
    // If no payments were on time, score is 0
    else if (paidOnTimeAmount < 1) onTime = 0;
    // Calculate on time percentage
    else onTime = Math.ceil((paidOnTimeAmount / totalAmount) * 100);

    let paid = 0;
    // If no payments yet, score is max
    if (!payments.length) paid = 100;
    // If no payments were paid, score is 0
    else if (paidAmount < 1) paid = 0;
    // Calculate paid percentage
    else paid = Math.ceil((paidAmount / totalAmount) * 100);

    return Math.ceil((onTime * 30) / 100 + (paid * 70) / 100);
  };

  /* ====================== GET BACKGROUND SCORES ====================== */
  const getBackgroundScores = () => {
    // Get Latest Screening status
    const status = customer.screeningStatus;
    if (!status) return 80;
    else if (status.toLowerCase() === "approved") return 100;
    else if (status.toLowerCase() === "declined") return 0;
    else return 80;
  };

  /* ====================== GET BACKGROUND SCORES ====================== */
  const getBehaviorScore = async (customerId) => {
    return new Promise(async (resolve, reject) => {
      try {
        setLoading(true);
        const bookings = await requestAdminAPI("filter", {
          table: "booking",
          filters: [`customerId: {contains: "${customerId}" }`],
        });
        let sum = 0;
      if (!bookings.length) sum = 5;
        bookings.forEach((booking) => {
          booking.rating ? (sum += booking.rating) : (sum += 5);
        });
        const average = Math.ceil(sum / (bookings.length || 1));
        setLoading(false);
        resolve(average * 20);
      } catch (err) {
        reject(err);
        setLoading(false);
        console.error(err);
      }
    });
  };

  /* ====================== GET COLOR ====================== */
  const getColor = (score) => {
    return score >= 90
      ? "text-green-600"
      : score >= 80
      ? "text-yellow-500"
      : score >= 60
      ? "text-orange-500"
      : "text-red-600";
  };

  // Get scores on load
  useEffect(() => {
    if (customer?.id) getScores();
  }, [customer]);

  if (loading)
    return (
      <div className="grid gap-4 justify-items-center">
        <div className="h-16 rounded-md w-28 bg-stone-700 animate-pulse" />
        <div className="grid grid-cols-4 gap-6">
          <div className="w-12 h-8 rounded-md bg-stone-700 animate-pulse" />
          <div className="w-12 h-8 rounded-md bg-stone-700 animate-pulse" />
          <div className="w-12 h-8 rounded-md bg-stone-700 animate-pulse" />
          <div className="w-12 h-8 rounded-md bg-stone-700 animate-pulse" />
        </div>
      </div>
    );

  return (
    <div className="grid gap-4 justify-items-center">
      <div className="text-center" title="Customer Score">
        <p className="text-stone-400">Customer Score</p>
        <p className={`text-5xl font-bold ${getColor(scores.average)}`}>
          {scores.average}
        </p>
      </div>
      <div className="grid grid-cols-4 gap-6">
        <div
          className="flex items-center space-x-1 clickable"
          onClick={() =>
            window.open(
              `${samsaraDriverLink}${customer.samsaraId}?duration=${duration}&end_ms=${endMs}`,
              "_blank"
            )
          }
          title="Driving Score"
        >
          <MdDriveEta className={`text-xl ${getColor(scores.driving)}`} />
          <p className={`text-xl font-bold ${getColor(scores.driving)}`}>
            {scores.driving}
          </p>
        </div>

        <div
          onClick={(e) =>
            navigate(
              `/customers/payments?id=${customer.id}&returnTo=/customers/info?id=${customer.id}`,
              e
            )
          }
          className="flex items-center space-x-1 clickable"
          title="Payment Score"
        >
          <MdPayments
            className={`text-xl ${getColor(scores.payment)}`}
            title="Payment score is based on a weighted average of total paid amount and amount of invoices paid on time."
          />
          <p className={`text-xl font-bold ${getColor(scores.payment)}`}>
            {scores.payment}
          </p>
        </div>

        <div className="flex items-center space-x-1" title="Behavior Score">
          <MdPeopleAlt className={`text-xl ${getColor(scores.behavior)}`} />
          <p className={`text-xl font-bold ${getColor(scores.behavior)}`}>
            {scores.behavior}
          </p>
        </div>

        <div className="flex items-center space-x-1" title="Background Score">
          <MdDocumentScanner
            className={`text-xl ${getColor(scores.background)}`}
            title="Background score is determined by screening the customer. An approved screening results in 100 score, declined results in 0, and no screening defaults to 80. "
          />
          <p className={`text-xl font-bold ${getColor(scores.background)}`}>
            {scores.background}
          </p>
        </div>
      </div>
    </div>
  );
}
